import { useFormValidation } from '@react-aria/form';
import { useFormReset, useSlotId } from '@react-aria/utils';
import { useFormValidationState } from '@react-stately/form';
import clsx from 'clsx';
import { type ForwardedRef, useRef, useState } from 'react';
import { useId } from 'react-aria';
import {
  FieldErrorContext as RACFieldErrorContext,
  FileTrigger as RACFileTrigger,
  type FileTriggerProps as RACFileTriggerProps,
  LabelContext as RACLabelContext,
  Provider as RACProvider,
  TextContext as RACTextContext,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useLocalizedMessages } from '../../i18n/index.js';
import { withStyleProps } from '../../with-style-props.js';
import { Button } from '../button/button.js';
import { FormFieldDescription, FormFieldError, FormFieldLabel } from '../internal/form-field/form-field.js';
import type { CommonProps, FieldProps } from '../types.js';
import { Text } from '../typography/text.js';
import { UNSTABLE_VisuallyHidden } from '../visually-hidden/visually-hidden.js';

export interface UNSTABLE_FilePickerProps
  extends
    CommonProps,
    Omit<FieldProps<File[]>, 'aria-label'>,
    Pick<RACFileTriggerProps, 'acceptedFileTypes' | 'allowsMultiple' | 'acceptDirectory' | 'defaultCamera'> {
  /** Whether the field is disabled. */
  isDisabled?: boolean;
  /** Whether the field is required. */
  isRequired?: boolean;
  /** Whether the field is invalid. */
  isInvalid?: boolean;
  /** The allowed file types that can be selected. */
  acceptedFileTypes?: string[];
  /** Whether multiple files can be selected. */
  allowsMultiple?: boolean;
  /** Handler called when files are selected. */
  onSelect?: (files: File[] | null) => void;
}

function UNSTABLE_FilePicker(
  {
    id,
    label,
    description,
    isDisabled,
    isRequired,
    isInvalid,
    onSelect,
    acceptedFileTypes,
    allowsMultiple,
    acceptDirectory,
    defaultCamera,
    error,
    form,
    name,
    validate,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-details': ariaDetails,
    UNSAFE_className,
    UNSAFE_style,
    ...props
  }: UNSTABLE_FilePickerProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const buttonId = useId(id);
  const labelId = useId();
  const fileTextId = useId();

  const [files, setFiles] = useState<File[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleSelect = (files: FileList | null) => {
    // Commit validation to clear any previous validation errors when new files are selected
    validationState.commitValidation();

    const filesArray = files?.length ? Array.from(files) : null;
    setFiles(filesArray);
    onSelect?.(filesArray);

    if (fileInputRef.current) {
      fileInputRef.current.files = files;
    }
  };

  const labelMessages = useLocalizedMessages('label');
  const messages = useLocalizedMessages('filePicker');
  const labelText =
    files?.length != null
      ? files.length === 1
        ? files[0]?.name
        : messages.format('multipleFilesSelected', { fileCount: files.length })
      : messages.format('noFilesSelected', { allowsMultiple: allowsMultiple ?? false });

  const validationBehavior = 'native';
  const validationState = useFormValidationState({
    value: files,
    isInvalid,
    isRequired,
    name,
    validate,
    validationBehavior,
  });
  useFormValidation({ isInvalid, isRequired, validate, validationBehavior }, validationState, fileInputRef);
  useFormReset(fileInputRef, null, setFiles);

  const descriptionId = useSlotId([Boolean(description)]);
  const errorMessageId = useSlotId([
    Boolean(error || validationState.displayValidation.validationErrors),
    validationState.displayValidation.isInvalid,
  ]);

  return (
    <RACProvider
      values={[
        [RACLabelContext, label != null ? { id: labelId, htmlFor: buttonId } : {}],
        [RACTextContext, { slots: { description: { id: descriptionId }, errorMessage: { id: errorMessageId } } }],
        [RACFieldErrorContext, validationState.displayValidation],
      ]}
    >
      <div {...props} className={clsx('cim-file-picker', UNSAFE_className)} style={UNSAFE_style} ref={ref}>
        {label && (
          <FormFieldLabel isRequired={isRequired} isDisabled={isDisabled}>
            {label}
            {/*
             * Button elements don't support `aria-required`, so we need to add visually hidden text to indicate that the field is required.
             * See: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Attributes/aria-required#examples:~:text=Note%3A%20If%20the%20field%27s%20label%20already%20contains%20the%20word%20%22required%22%2C%20it%20is%20recommended%20to%20leave%20out%20the%20aria%2Drequired%20attribute.%20This%20avoids%20that%20screen%20readers%20read%20out%20the%20term%20%22required%22%20twice.
             */}
            {isRequired && <UNSTABLE_VisuallyHidden>{labelMessages.format('required')}</UNSTABLE_VisuallyHidden>}
          </FormFieldLabel>
        )}
        <div className="cim-file-picker-button-container">
          <RACFileTrigger
            onSelect={handleSelect}
            acceptedFileTypes={acceptedFileTypes}
            allowsMultiple={allowsMultiple}
            acceptDirectory={acceptDirectory}
            defaultCamera={defaultCamera}
          >
            <Button
              id={buttonId}
              isDisabled={isDisabled}
              aria-labelledby={[buttonId, label != null ? labelId : null, ariaLabelledBy].filter(Boolean).join(' ')}
              aria-describedby={[fileTextId, descriptionId, errorMessageId, ariaDescribedBy].join(' ')}
              aria-details={ariaDetails}
            >
              {messages.format('chooseFile')}
            </Button>
          </RACFileTrigger>
          <Text id={fileTextId} as="p" variant="medium" tone="base">
            {labelText}
          </Text>
        </div>
        <FormFieldError>{error}</FormFieldError>
        <FormFieldDescription>{description}</FormFieldDescription>
      </div>
      {name && (
        <input
          type="file"
          className="cim-file-picker-hidden-input"
          form={form}
          name={name}
          multiple={allowsMultiple}
          required={isRequired}
          ref={fileInputRef}
        />
      )}
    </RACProvider>
  );
}

const _UNSTABLE_FilePicker = withStyleProps(forwardRef(UNSTABLE_FilePicker), 'FilePicker');

export { _UNSTABLE_FilePicker as UNSTABLE_FilePicker };
